import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  calculateRWP,
  rouletteWheelSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';

/**
 * SFLA-GA Hybrid (SFLAGA) implementation from scratch
 * Combines SFLA memeplex structure with GA operators
 */

/**
 * Divides population into memeplexes
 */
function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

/**
 * Shuffles memeplexes back into population
 */
function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  let idx = 0;
  for (const memeplex of memeplexes) {
    for (const frog of memeplex) {
      population[idx++] = frog;
    }
  }
}

/**
 * Evolves a memeplex using GA operators
 */
function evolveMemeplex(
  memeplex: Chromosome[],
  globalBest: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  crossoverRate: number,
  mutationRate: number
): void {
  const memeplexSize = memeplex.length;
  const crossoverCount = Math.floor((memeplexSize * crossoverRate) / 100);
  const mutateCount = Math.floor((memeplexSize * mutationRate) / 100);

  // Calculate RWP for memeplex
  calculateRWP(memeplex);

  // Crossover operations (using global best for some crossovers)
  for (let i = 0; i < crossoverCount; i++) {
    let parent1: Chromosome;
    let parent2: Chromosome;

    // Use global best as one parent for some crossovers
    if (i % 3 === 0 && memeplex.length > 0) {
      parent1 = globalBest;
      parent2 = memeplex[rouletteWheelSelection(memeplex)];
    } else {
      parent1 = memeplex[rouletteWheelSelection(memeplex)];
      parent2 = memeplex[rouletteWheelSelection(memeplex)];
    }

    const [child1] = singlePointCrossover(parent1, parent2, taskCount);
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);

    // Replace worst in memeplex if better
    memeplex.sort((a, b) => b.fitness - a.fitness);
    const worstIdx = memeplex.length - 1;
    if (child1.fitness > memeplex[worstIdx].fitness) {
      memeplex[worstIdx] = child1;
    }
  }

  // Mutation operations (reduced rate for stability)
  for (let i = 0; i < mutateCount; i++) {
    const idx = Math.floor(Math.random() * memeplexSize);
    const mutated = mutate(memeplex[idx], taskCount, serviceCount);
    calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);

    if (mutated.fitness > memeplex[idx].fitness) {
      memeplex[idx] = mutated;
    }
  }
}

/**
 * Runs SFLAGA for specified generations
 */
export async function runSFLAGA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    memeplexCount: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Create memeplexes
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    // Find global best
    const globalBest = findBestChromosome(population);

    // Evolve each memeplex using GA operators
    for (const memeplex of memeplexes) {
      evolveMemeplex(
        memeplex,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        parameters.crossoverRate,
        parameters.mutationRate
      );
    }

    // Shuffle memeplexes
    shuffleMemeplexes(memeplexes, population);

    // Recreate memeplexes
    memeplexes = createMemeplexes(population, parameters.memeplexCount);

    // Report progress
    const best = findBestChromosome(population);
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, best.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return findBestChromosome(population);
}

